import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Container } from '@mui/material';
import {
  exchangeCode,
  storeTokens,
  SSO_STORAGE_KEY_VERIFIER,
  SSO_STORAGE_KEY_STATE,
} from '@nota/sso';
import { getSsoConfig } from '../config/ssoConfig';
import { bridgeTokens, consumeFromPath, clearSsoSession } from '../services/ssoService';
import { fetchCurrentUser } from '../services/meService';
import authService from '../services/authService';
import { useAuth } from '../context/AuthContext';

const CallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { completeSsoLogin } = useAuth();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const handleCallback = async () => {
      const idpError = searchParams.get('error');
      if (idpError) {
        const desc = searchParams.get('error_description') || idpError;
        navigate('/login', { replace: true, state: { ssoError: desc } });
        return;
      }

      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        navigate('/login', {
          replace: true,
          state: { ssoError: 'Missing authorization code or state parameter' },
        });
        return;
      }

      const storedState = sessionStorage.getItem(SSO_STORAGE_KEY_STATE);
      if (state !== storedState) {
        navigate('/login', {
          replace: true,
          state: { ssoError: 'Invalid state parameter — possible CSRF attack' },
        });
        return;
      }

      const codeVerifier = sessionStorage.getItem(SSO_STORAGE_KEY_VERIFIER);
      if (!codeVerifier) {
        navigate('/login', {
          replace: true,
          state: { ssoError: 'Missing code verifier' },
        });
        return;
      }

      try {
        const config = getSsoConfig();
        const tokenResponse = await exchangeCode(config, code, codeVerifier);

        storeTokens(tokenResponse);
        bridgeTokens();

        sessionStorage.removeItem(SSO_STORAGE_KEY_STATE);
        sessionStorage.removeItem(SSO_STORAGE_KEY_VERIFIER);

        let user;
        try {
          user = await fetchCurrentUser();
        } catch (hydrationError: any) {
          clearSsoSession();
          const status = hydrationError?.response?.status;
          const errorCode = hydrationError?.response?.data?.code;
          if (status === 403 && errorCode === 'NOTA_USER_NOT_MAPPED') {
            navigate('/login', {
              replace: true,
              state: {
                ssoError:
                  'This Nota account is not authorized for the wine app. Ask an administrator to add your email to the wine-api admin user list.',
              },
            });
            return;
          }
          navigate('/login', {
            replace: true,
            state: {
              ssoError: `Could not load your profile after sign-in (${status ?? 'network error'}). Please try again.`,
            },
          });
          return;
        }

        localStorage.setItem('user', JSON.stringify(user));
        completeSsoLogin(user);

        const from = consumeFromPath();
        const target = authService.computePostLoginRedirect(from);
        navigate(target, { replace: true });
      } catch (err: any) {
        clearSsoSession();
        navigate('/login', {
          replace: true,
          state: { ssoError: err.message || 'Token exchange failed' },
        });
      }
    };

    handleCallback();
  }, [navigate, searchParams, completeSsoLogin]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Signing in...
        </Typography>
      </Box>
    </Container>
  );
};

export default CallbackPage;
